// ── Types ──────────────────────────────────────────────────────────────────────

/** Mirrors api/archives.ts's ArchiveStandingRow — kept local so this file has no dependency
 * on the api layer, matching how TrophyMember is a standalone shape too. */
export interface ArchiveStandingRow {
  rank: number;
  displayName: string;
  score: number;
  isTied: boolean;
  userId: string | null;
}

export interface TrophyMember {
  id: string;
  displayName: string;
  score: number;
  delta: number;
  gwScoreHistory: { gwNumber: number; score: number }[];
  targetState?: "BUST" | "ELIGIBLE" | "INELIGIBLE" | null;
  becameEligibleThisGw?: boolean;
  exactHits?: number;
  /** Raw goal count — what TARGET's target/BUST threshold is actually defined on. score is a
   * points value (sum of calcPoints(goals)) that is only numerically equal while calcPoints is
   * the identity function; falls back to score for callers/fixtures that don't send it. */
  totalGoals?: number;
  /** Gameweek number of this member's first-ever score > 0, from their full history — not the
   * 5-GW window gwScoreHistory is truncated to. Undefined/null means never scored, or the
   * backend hasn't computed it (older/mocked responses). */
  firstScoringGw?: number | null;
  /** Current consecutive scoring gameweeks, from the full history. Falls back to a
   * gwScoreHistory-derived estimate when absent. */
  scoringStreak?: number;
}

export interface Trophy {
  name: string;
  description: string;
  recipients: string[]; // displayNames
  /** "superlative" trophies ("best/worst at X") are suppressed once too much of the league wins
   * them — see isSelective. "milestone" trophies ("X happened to you this GW") are never
   * suppressed: e.g. eight members going bust in one gameweek is real news, not noise. */
  kind: "superlative" | "milestone";
}

type LeagueMode = "TARGET" | "FINAL_POSITIONS" | "GAMEWEEK_CHALLENGE" | "SCORE_PREDICTOR" | "SYNDICATE";
type LeagueStatus = "PREPARING" | "RUNNING" | "COMPLETED";

// ── Helpers ────────────────────────────────────────────────────────────────────

function topScorers(members: TrophyMember[], getValue: (m: TrophyMember) => number): string[] {
  const scores = members.map(getValue);
  const max = Math.max(...scores);
  if (max <= 0) return [];
  return members.filter((m) => getValue(m) === max).map((m) => m.displayName);
}

function bottomMembers(members: TrophyMember[]): string[] {
  if (members.length === 0) return [];
  const min = Math.min(...members.map((m) => m.score));
  // Only award if more than one member (no point in awarding to everyone)
  if (members.every((m) => m.score === min)) return [];
  return members.filter((m) => m.score === min).map((m) => m.displayName);
}

function leagueAverage(members: TrophyMember[]): number {
  if (members.length === 0) return 0;
  const scores = members.map((m) => m.delta);
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

function currentStreak(history: { gwNumber: number; score: number }[]): number {
  const sorted = [...history].sort((a, b) => b.gwNumber - a.gwNumber);
  let streak = 0;
  for (const entry of sorted) {
    if (entry.score > 0) streak++;
    else break;
  }
  return streak;
}

function offTheMarkThisGw(m: TrophyMember): boolean {
  const sorted = [...m.gwScoreHistory].sort((a, b) => a.gwNumber - b.gwNumber);
  if (sorted.length === 0) return false;
  const last = sorted[sorted.length - 1];
  if (last.score <= 0) return false;
  // Prefer firstScoringGw (computed from the member's full history) over the 5-GW-truncated
  // gwScoreHistory window — otherwise someone who scored long ago, went quiet for more than 5
  // GWs, then scored again would wrongly look like they're scoring "for the first time".
  if (m.firstScoringGw != null) return last.gwNumber === m.firstScoringGw;
  return sorted.slice(0, -1).every((e) => e.score === 0);
}

function onFireThisGw(m: TrophyMember, avg: number, minGws = 3): boolean {
  const sorted = [...m.gwScoreHistory].sort((a, b) => b.gwNumber - a.gwNumber);
  if (sorted.length < minGws) return false;
  return sorted.slice(0, minGws).every((e) => e.score > avg);
}

// ── Podium trophies (completed leagues) ───────────────────────────────────────

function podiumTrophies(members: TrophyMember[], mode: LeagueMode): Trophy[] {
  const sorted = [...members].sort((a, b) => {
    if (mode === "TARGET") {
      const aBust = a.targetState === "BUST", bBust = b.targetState === "BUST";
      if (aBust !== bBust) return aBust ? 1 : -1;
      if (aBust && bBust) return a.score - b.score;
    }
    return b.score - a.score;
  });

  const trophies: Trophy[] = [];
  const medals = [
    { place: 1, name: "🥇 Champion" },
    { place: 2, name: "🥈 Runner-up" },
    { place: 3, name: "🥉 Third Place" },
  ];

  let i = 0;
  for (const medal of medals) {
    if (i >= sorted.length) break;
    const groupScore = sorted[i].score;
    const tied = sorted.filter((m) => m.score === groupScore);
    trophies.push({
      name: medal.name,
      description: `Finished ${medal.place === 1 ? "1st" : medal.place === 2 ? "2nd" : "3rd"} in the league`,
      recipients: tied.map((m) => m.displayName),
      kind: "milestone", // podium places must never be suppressed by the selectivity guard
    });
    i += tied.length;
    if (i >= sorted.length) break;
  }

  return trophies;
}

// ── Archive trophies (frozen leagues — standings only, no live TrophyMember data) ──

/**
 * Groups standings by their already-frozen `rank` value, sorted ascending — each group is one
 * placement (index 0 = winner's group, index 1 = the next distinct group, etc). Exported so
 * archiveSummary.ts's season-summary computation can share the exact same grouping.
 *
 * Deliberately index-based, not rank-number-based: a tie for 1st still leaves index 1 as "the
 * next group" even though its raw `rank` value is 3, not 2 (rank numbers skip past ties — see
 * DECISIONS.md 2026-08-12). Matching on `rank === 2` directly would wrongly skip Runner-up
 * whenever 1st is tied, the same "shared podium" case the original podiumTrophies (above) and
 * the backend's generateSeasonSummaryEvents both handle by group order, not by rank number.
 */
export function groupStandingsByRank(standings: ArchiveStandingRow[]): ArchiveStandingRow[][] {
  const byRank = new Map<number, ArchiveStandingRow[]>();
  for (const s of standings) {
    const group = byRank.get(s.rank);
    if (group) group.push(s);
    else byRank.set(s.rank, [s]);
  }
  return Array.from(byRank.entries()).sort(([a], [b]) => a - b).map(([, group]) => group);
}

/** Podium trophies for an archived league — see groupStandingsByRank for why this groups by
 * placement order rather than by matching a fixed rank number. */
export function archiveTrophies(standings: ArchiveStandingRow[]): Trophy[] {
  const groups = groupStandingsByRank(standings);
  const medals = [
    { name: "🥇 Champion", place: "1st" },
    { name: "🥈 Runner-up", place: "2nd" },
    { name: "🥉 Third Place", place: "3rd" },
  ];

  const trophies: Trophy[] = [];
  medals.forEach((medal, i) => {
    const group = groups[i];
    if (!group) return;
    trophies.push({
      name: medal.name,
      description: `Finished ${medal.place} in the league`,
      recipients: group.map((s) => s.displayName),
      kind: "milestone",
    });
  });

  return trophies;
}

// ── Weekly trophies ────────────────────────────────────────────────────────────

const MIN_GWS_FOR_PACE = 3;
const PACE_TOLERANCE_FRACTION = 0.1;

function targetTrophies(members: TrophyMember[], target: number, totalGws: number, completedGws: number): Trophy[] {
  const trophies: Trophy[] = [];
  const eligible = members.filter((m) => m.targetState !== "BUST");

  const offMark = members.filter(offTheMarkThisGw).map((m) => m.displayName);
  if (offMark.length) trophies.push({ name: "Off the Mark", description: "First gameweek with a score", recipients: offMark, kind: "milestone" });

  const bust = members.filter((m) => m.targetState === "BUST" && m.delta > 0 && m.score - m.delta <= target).map((m) => m.displayName);
  if (bust.length) trophies.push({ name: "Bust!", description: "Crossed the target threshold", recipients: bust, kind: "milestone" });

  const triggerHappy = topScorers(members, (m) => m.delta);
  if (triggerHappy.length) trophies.push({ name: "Trigger Happy", description: "Highest score", recipients: triggerHappy, kind: "superlative" });

  if (completedGws >= MIN_GWS_FOR_PACE && totalGws > 0 && target > 0) {
    const expected = (target * completedGws) / totalGws;
    const tolerance = Math.max(1, target * PACE_TOLERANCE_FRACTION);
    const onPace = members
      .filter((m) => m.targetState === "ELIGIBLE")
      .filter((m) => Math.abs((m.totalGoals ?? m.score) - expected) <= tolerance)
      .map((m) => m.displayName);
    if (onPace.length) trophies.push({ name: "On Pace", description: "On track to hit the target", recipients: onPace, kind: "superlative" });
  }

  const fullHouse = members.filter((m) => m.becameEligibleThisGw).map((m) => m.displayName);
  if (fullHouse.length) trophies.push({ name: "Full House", description: "All players finally on the scoresheet", recipients: fullHouse, kind: "milestone" });

  const deadLast = bottomMembers(eligible.length > 0 ? eligible : members);
  if (deadLast.length) trophies.push({ name: "Dead Last", description: "Bottom of the table", recipients: deadLast, kind: "superlative" });

  if (completedGws > 1) {
    const comeback = topScorers(members, (m) => m.delta - (m.gwScoreHistory.slice(-2, -1)[0]?.score ?? 0));
    if (comeback.length) trophies.push({ name: "Comeback Kid", description: "Biggest score jump", recipients: comeback, kind: "superlative" });
  }

  return trophies;
}

function gwcTrophies(members: TrophyMember[]): Trophy[] {
  const trophies: Trophy[] = [];
  const avg = leagueAverage(members);

  const offMark = members.filter(offTheMarkThisGw).map((m) => m.displayName);
  if (offMark.length) trophies.push({ name: "Off the Mark", description: "First gameweek with a score", recipients: offMark, kind: "milestone" });

  const topGun = topScorers(members, (m) => m.delta);
  if (topGun.length) trophies.push({ name: "Top Gun", description: "Highest score", recipients: topGun, kind: "superlative" });

  const deadLast = bottomMembers(members);
  if (deadLast.length) trophies.push({ name: "Dead Last", description: "Bottom of the table", recipients: deadLast, kind: "superlative" });

  if (members.some((m) => m.gwScoreHistory.length >= 2)) {
    const comeback = topScorers(members, (m) => m.delta - (m.gwScoreHistory.slice(-2, -1)[0]?.score ?? 0));
    if (comeback.length) trophies.push({ name: "Comeback Kid", description: "Biggest score jump", recipients: comeback, kind: "superlative" });
  }

  const onFire = members.filter((m) => onFireThisGw(m, avg)).map((m) => m.displayName);
  if (onFire.length) trophies.push({ name: "On Fire", description: "Above average 3+ gameweeks running", recipients: onFire, kind: "superlative" });

  // Streak: longest current consecutive scoring GWs (min 2). Prefer the backend-computed
  // scoringStreak (full history) over the local gwScoreHistory-derived fallback, which is
  // silently capped at 5 GWs by the truncated window.
  const streaks = members.map((m) => ({ displayName: m.displayName, streak: m.scoringStreak ?? currentStreak(m.gwScoreHistory) }));
  const maxStreak = Math.max(...streaks.map((s) => s.streak));
  if (maxStreak >= 2) {
    const streakWinners = streaks.filter((s) => s.streak === maxStreak);
    trophies.push({
      name: `Streak · ${maxStreak} GWs`,
      description: `Longest current scoring streak`,
      recipients: streakWinners.map((s) => s.displayName),
      kind: "superlative",
    });
  }

  return trophies;
}

function fpTrophies(members: TrophyMember[]): Trophy[] {
  const trophies: Trophy[] = [];

  const deadLast = bottomMembers(members);
  if (deadLast.length) trophies.push({ name: "Dead Last", description: "Bottom of the table", recipients: deadLast, kind: "superlative" });

  // Crystal Ball, Lucky Break, and Comeback Kid used to be three trophies computing the exact
  // same topScorers(delta) expression — always identical recipients under three names, two of
  // which ("Most correct predictions", "Most ranks gained") didn't even describe what delta is.
  // Consolidated to one, with a description that matches what's actually measured.
  const crystalBall = topScorers(members, (m) => m.delta);
  if (crystalBall.length) trophies.push({ name: "Crystal Ball", description: "Biggest score gain this gameweek", recipients: crystalBall, kind: "superlative" });

  const toughLuck = topScorers(members, (m) => -m.delta);
  if (toughLuck.length && members.some((m) => m.delta < 0)) {
    trophies.push({ name: "Tough Luck", description: "Biggest score drop", recipients: toughLuck, kind: "superlative" });
  }

  return trophies;
}

function scorePredictorTrophies(members: TrophyMember[]): Trophy[] {
  const trophies: Trophy[] = [];
  const avg = leagueAverage(members);

  const offMark = members.filter(offTheMarkThisGw).map((m) => m.displayName);
  if (offMark.length) trophies.push({ name: "Off the Mark", description: "First gameweek with a score", recipients: offMark, kind: "milestone" });

  const sharpShooter = topScorers(members, (m) => m.delta);
  if (sharpShooter.length) trophies.push({ name: "Sharp Shooter", description: "Highest points this gameweek", recipients: sharpShooter, kind: "superlative" });

  const crystalBall = members.filter((m) => (m.exactHits ?? 0) > 0);
  if (crystalBall.length) {
    const maxExact = Math.max(...crystalBall.map((m) => m.exactHits!));
    const winners = crystalBall.filter((m) => m.exactHits === maxExact).map((m) => m.displayName);
    trophies.push({ name: "Crystal Ball", description: "Most exact scorelines this gameweek", recipients: winners, kind: "superlative" });
  }

  const deadLast = bottomMembers(members);
  if (deadLast.length) trophies.push({ name: "Dead Last", description: "Bottom of the table", recipients: deadLast, kind: "superlative" });

  if (members.some((m) => m.gwScoreHistory.length >= 2)) {
    const comeback = topScorers(members, (m) => m.delta - (m.gwScoreHistory.slice(-2, -1)[0]?.score ?? 0));
    if (comeback.length) trophies.push({ name: "Comeback Kid", description: "Biggest score jump", recipients: comeback, kind: "superlative" });
  }

  const onFire = members.filter((m) => onFireThisGw(m, avg)).map((m) => m.displayName);
  if (onFire.length) trophies.push({ name: "On Fire", description: "Above average 3+ gameweeks running", recipients: onFire, kind: "superlative" });

  return trophies;
}

// SYNDICATE has no mode-specific fields to award on (no target/BUST, no exactHits) — only the
// mode-agnostic score/delta/gwScoreHistory fields every mode already sends. Same formulas as
// gwcTrophies' equivalents.
function syndicateTrophies(members: TrophyMember[]): Trophy[] {
  const trophies: Trophy[] = [];

  const offMark = members.filter(offTheMarkThisGw).map((m) => m.displayName);
  if (offMark.length) trophies.push({ name: "Off the Mark", description: "First gameweek with a score", recipients: offMark, kind: "milestone" });

  const triggerHappy = topScorers(members, (m) => m.delta);
  if (triggerHappy.length) trophies.push({ name: "Trigger Happy", description: "Highest score", recipients: triggerHappy, kind: "superlative" });

  const deadLast = bottomMembers(members);
  if (deadLast.length) trophies.push({ name: "Dead Last", description: "Bottom of the table", recipients: deadLast, kind: "superlative" });

  if (members.some((m) => m.gwScoreHistory.length >= 2)) {
    const comeback = topScorers(members, (m) => m.delta - (m.gwScoreHistory.slice(-2, -1)[0]?.score ?? 0));
    if (comeback.length) trophies.push({ name: "Comeback Kid", description: "Biggest score jump", recipients: comeback, kind: "superlative" });
  }

  return trophies;
}

// ── Main export ────────────────────────────────────────────────────────────────

/** A superlative trophy won by more than half the league has stopped being a superlative — it's
 * noise, not recognition. Milestone trophies are exempt: several members going bust or getting
 * off the mark in the same gameweek is real news, and is handled by collapsing the recipient
 * list in the UI (TrophiesPanel), not by hiding the trophy. */
function isSelective(trophy: Trophy, memberCount: number): boolean {
  return trophy.kind === "milestone" || trophy.recipients.length <= Math.max(1, Math.floor(memberCount / 2));
}

export function computeTrophies(opts: {
  mode: LeagueMode;
  status: LeagueStatus;
  members: TrophyMember[];
  target?: number;
  totalGws?: number;
  completedGws?: number;
}): Trophy[] {
  const { mode, status, members, target = 0, totalGws = 0, completedGws = 0 } = opts;

  if (status === "PREPARING") return [];

  if (status === "COMPLETED") {
    return podiumTrophies(members, mode);
  }

  // RUNNING: weekly trophies
  let trophies: Trophy[];
  switch (mode) {
    case "TARGET":             trophies = targetTrophies(members, target, totalGws, completedGws); break;
    case "GAMEWEEK_CHALLENGE": trophies = gwcTrophies(members); break;
    case "FINAL_POSITIONS":    trophies = fpTrophies(members); break;
    case "SCORE_PREDICTOR":    trophies = scorePredictorTrophies(members); break;
    case "SYNDICATE":          trophies = syndicateTrophies(members); break;
    default:                   trophies = []; break;
  }
  return trophies.filter((t) => isSelective(t, members.length));
}
